import React, { useState, useEffect, useOptimistic, useTransition, useRef } from "react"
import "../table/table.scss"
import InputCheckbox from "../inputs/inputCheckbox/inputCheckbox"
import Pagination from "../pagination/pagination"
import { MdSearchOff } from "react-icons/md"
import Input from "../inputs/input/input"
import Notification from "../notification/notification"

const TableEditable = ({
	columns,
	data,
	noResultsMessage,
	showHeaderCheckbox = true,
	onRowSelect,
	tableEditMode,
	setTableEditMode,
	onCellEdit, // función para guardar el cambio de cada elemento (llamada a API)
	onUpdate, // función para actualizar los datos masivamente
	rowKey,
	statesRestrictEdit = [],
	initalCurrentPage = 1,
	rowClassName // Nueva propiedad para clases de fila personalizadas
}) => {
	const [currentPage, setCurrentPage] = useState(initalCurrentPage)
	const [selectedRows, setSelectedRows] = useState([])
	const rowsPerPage = 7
	const [sortConfig, setSortConfig] = useState({ key: null, direction: null })
	const [isPending, startTransition] = useTransition()
	const [selectedAll, setSelectedAll] = useState(false)
	const notificationRef = useRef(null)

	// Estado local para los datos
	const [localData, setLocalData] = useState(data)

	useEffect(() => {
  setCurrentPage(1);
}, [data]);

	// Usamos useOptimistic para manejar las actualizaciones de datos
	const [optimisticData, addOptimisticUpdate] = useOptimistic(
		localData,
		(state, update) => {
			// update contendrá { rowId, columnKey, value }
			return state.map((row) =>
				row[rowKey] === update.rowId
					? { ...row, [update.columnKey]: update.value }
					: row
			)
		}
	)

	// Actualizamos el estado local cuando cambian los datos externos
	useEffect(() => {
		setLocalData(data)
	}, [data])

	// Estado para edición individual: guarda la celda que se está editando y su valor
	const [editingCell, setEditingCell] = useState(null) // { rowId, columnKey }
	const [editingCellValue, setEditingCellValue] = useState("")

	const indexOfLastRow = currentPage * rowsPerPage
	const indexOfFirstRow = indexOfLastRow - rowsPerPage

	// Ordenar los datos según la configuración
	const sortedData = React.useMemo(() => {
		let sortableData = [...optimisticData]
		if (sortConfig.key) {
			sortableData.sort((a, b) => {
				if (a[sortConfig.key] < b[sortConfig.key]) {
					return sortConfig.direction === "ascending" ? -1 : 1
				}
				if (a[sortConfig.key] > b[sortConfig.key]) {
					return sortConfig.direction === "ascending" ? 1 : -1
				}
				return 0
			})
		}
		return sortableData
	}, [optimisticData, sortConfig])

	const currentRows = sortedData.slice(indexOfFirstRow, indexOfLastRow)
	const totalPages = Math.ceil(optimisticData.length / rowsPerPage)

	const handleSelectRow = (rowId) => {
		setSelectedRows((prevSelectedRows) => {
			const newSelectedRows = prevSelectedRows.includes(rowId)
				? prevSelectedRows.filter((id) => id !== rowId)
				: [...prevSelectedRows, rowId]
			// let selectable = true
			// if (newSelectedRows.includes(rowId)) {
			// 	selectable = isSelectable(localData.find((r) => r[rowKey] === rowId))
			// }
			if (onRowSelect ) {
				onRowSelect(newSelectedRows)
			}
			//  else if (!selectable) {
			// 	notificationRef.current.addNotification(
			// 		"No se puede seleccionar la tarifa",
			// 		"warning"
			// 	)
			// 	return prevSelectedRows
			// }
			return newSelectedRows
		})
	}

	const handleSelectAll = (e) => {
		const isChecked = e.target.checked
		if (isChecked) {
			setSelectedAll(true)
			// const selectableRows = optimisticData.filter((row) => isSelectable(row))

			// Mapear para obtener solo los IDs de las filas seleccionables
			const allRowIds = optimisticData.map((row) => row[rowKey])
			setSelectedRows(allRowIds)
			if (onRowSelect) {
				onRowSelect(allRowIds)
			}

			// if (optimisticData.some((row) => !isSelectable(row))) {
			// 	notificationRef.current.addNotification(
			// 		"Hay alguna tarifa que no se puede seleccionar",
			// 		"warning"
			// 	)
			// 	if(allRowIds.length === 0){
			// 		setSelectedAll(false)
			// 	}
			// }
		} else {
			setSelectedAll(false)
			setSelectedRows([])
			if (onRowSelect) {
				onRowSelect([])
			}
		}
	}

	//   const handleSelectAll = (e) => {
	//     const isChecked = e.target.checked;
	//     if (isChecked) {
	//         const allRowIds = data.map((row) => row.codMueble);
	//         setSelectedRows(allRowIds);
	//         if (onRowSelect) {
	//             onRowSelect(allRowIds);
	//         }
	//     } else {
	//         setSelectedRows([]);
	//         if (onRowSelect) {
	//             onRowSelect([]);
	//         }
	//     }
	// };

	const requestSort = (key) => {
		let direction = "ascending"
		if (sortConfig.key === key) {
			direction = sortConfig.direction === "ascending" ? "descending" : null
		}
		setSortConfig({ key: direction ? key : null, direction })
	}

	// Actualización en modo de edición global
	const handleTableCellChange = (rowId, columnKey, newValue) => {
		setLocalData((prevData) =>
			prevData.map((row) =>
				row[rowKey] === rowId ? { ...row, [columnKey]: newValue } : row
			)
		)
	}

	// Función para actualizar la celda en modo individual (doble clic)
	const handleCellUpdate = async (row, columnKey, newValue) => {
		// Primero agregamos la actualización optimista
		addOptimisticUpdate({ rowId: row[rowKey], columnKey, value: newValue })

		// Luego iniciamos la transición para el trabajo real
		startTransition(async () => {
			try {
				// Llamamos a la función de API pasando id, clave y nuevo valor
				if (onCellEdit && row[columnKey] !== newValue) {
					const success = await onCellEdit(row[rowKey], columnKey, newValue)

					// Si la API devuelve error, podríamos revertir el cambio optimista
					if (success === false) {
						// Aquí podrías manejar el error, quizás actualizando el estado local
						setLocalData((prevData) => [...prevData]) // Forzar actualización con datos originales
					} else {
						// Actualizamos el estado local con el valor confirmado
						setLocalData((prevData) =>
							prevData.map((r) =>
								r[rowKey] === row[rowKey] ? { ...r, [columnKey]: newValue } : r
							)
						)
					}
				}
			} catch (error) {
				console.error("Error al actualizar la celda:", error)
				// Revertimos a los datos originales en caso de error
				setLocalData((prevData) => [...prevData])
			} finally {
				setEditingCell(null)
			}
		})
	}

	// Manejo de eventos de teclado en edición individual
	const handleKeyDown = (e, row, columnKey, newValue) => {
		if (e.key === "Enter") {
			handleCellUpdate(row, columnKey, newValue)
		} else if (e.key === "Escape") {
			setEditingCell(null)
		}
	}

	// const isSelectable = (row) => {
	// 	console.log(statesRestrictEdit)
	// 	if(statesRestrictEdit.length > 0 && statesRestrictEdit.includes(row.codEstado)){
	// 		return false;
	// 	}
	// 	const requiredColumns = columns.filter((col) => col.required)
	// 	return requiredColumns.every(
	// 		(col) =>
	// 			row[col.key] !== undefined &&
	// 			row[col.key] !== null &&
	// 			row[col.key] !== "" &&
	// 			row[col.key] !== 0
	// 	)
	// }

	// Guardar cambios de la tabla completa en modo global
	// const saveTableEdits = async () => {
	//   if (onCellEdit) {
	//     const promises = [];
	//     localData.forEach((row) => {
	//       const originalRow = data.find((d) => d.codMueble === row.codMueble);
	//       if (originalRow) {
	//         Object.keys(row).forEach((key) => {
	//           if (row[key] !== originalRow[key]) {
	//             promises.push(onCellEdit(row.codMueble, key, row[key]));
	//           }
	//         });
	//       }
	//     });

	//     // Esperar a que todas las actualizaciones terminen
	//     try {
	//       await Promise.all(promises);
	//       // Actualizar el estado local con los cambios confirmados
	//       setLocalData([...localData]);
	//     } catch (error) {
	//       console.error("Error al guardar los cambios:", error);
	//     }
	//   }
	//   setTableEditMode(false);
	//   setEditingCell(null);
	// };

	useEffect(() => {
		async function syncData() {
			if (!tableEditMode && onUpdate) {
				try {
					// Filtrar solo los elementos modificados
					const modifiedData = localData.filter((row, index) => {
						const originalRow = data[index]
						return JSON.stringify(row) !== JSON.stringify(originalRow)
					})

					if (modifiedData.length > 0) {
						await onUpdate(modifiedData)
					}

					// Sincronizar con datos originales
					setLocalData([...optimisticData])
				} catch (error) {
					console.error("Error en actualización masiva:", error)
					// Restaurar datos originales en caso de error
					setLocalData([...data])
				}
				setEditingCell(null)
			}
		}
		syncData()
	}, [tableEditMode])

	const getHighlightClass = (days) => {
		if (days <= 0) return "days-red"
		if (days < 3) return "days-orange"
		return "days-green"
	}

	return (
		<div className="table-container table-editable-container">
			<table className="custom-table custom-table-editable">
				<thead>
					<tr>
						{showHeaderCheckbox && (
							<th>
								<InputCheckbox
									checked={selectedAll}
									onChange={handleSelectAll}
								/>
							</th>
						)}
						{columns.map((col) => (
							<th key={col.key} onClick={() => requestSort(col.key)}>
								{col.label}
								{sortConfig.key === col.key
									? sortConfig.direction === "ascending"
										? " ▲"
										: " ▼"
									: null}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{currentRows.length > 0 ? (
						currentRows.map((row) => (
							<tr key={row[rowKey]} className={rowClassName ? rowClassName(row) : ""}>
								{showHeaderCheckbox && (
									<td>
										<InputCheckbox
											checked={selectedRows.includes(row[rowKey])}
											onChange={() => handleSelectRow(row[rowKey])}
										/>
									</td>
								)}
								{columns.map((col) => {
									// Si estamos en modo edición global, usamos el estado local
									const cellValue = tableEditMode
										? localData.find((r) => r[rowKey] === row[rowKey])?.[
												col.key
										  ] || ""
										: row[col.key]

									// Verificamos si la celda está en edición individual
									const isEditing =
										!tableEditMode &&
										editingCell &&
										editingCell.rowId === row[rowKey] &&
										editingCell.columnKey === col.key

									const isEditable = typeof col.editable === "function" ? col.editable(row) : col.editable;

									return (
										<td
											key={`${row[rowKey]}-${col.key}`}
											title={cellValue}
											onDoubleClick={() => {
												if (!tableEditMode) {
													setEditingCell({
														rowId: row[rowKey],
														columnKey: col.key
													})
													setEditingCellValue(cellValue)
												}
											}}
											className={
												isEditing
													? "editing"
													: col.highlightDays
													? getHighlightClass(row[col.key])
													: (isEditable && !statesRestrictEdit.includes(row.codEstado))
													 ?
													"editable" : ""
											}
										>
											{tableEditMode && isEditable && !statesRestrictEdit.includes(row.codEstado) ? (
												<Input
													type={"text"}
													name={`${col.label}`}
													value={cellValue}
													onChange={(e) =>
														handleTableCellChange(
															row[rowKey],
															col.key,
															e.target.value
														)
													}
												/>
											) : isEditing && isEditable && !statesRestrictEdit.includes(row.codEstado) ? (
												<Input
													type={"text"}
													autoFocus
													name={`${col.label}`}
													value={editingCellValue}
													onChange={(e) => setEditingCellValue(e.target.value)}
													onBlur={() =>
														handleCellUpdate(row, col.key, editingCellValue)
													}
													onKeyDown={(e) =>
														handleKeyDown(e, row, col.key, editingCellValue)
													}
												/>
											) : col.render ? (
												col.render(row)
											) : (
												cellValue
											)}
										</td>
									)
								})}
							</tr>
						))
					) : (
						<tr>
							<td colSpan={columns.length + 2} className="no-results-cell">
								<div className="no-results-cell__content">
									<MdSearchOff className="no-results-cell__content__icon" />
									<p className="no-results-cell__content__text">
										{noResultsMessage || "No se han encontrado resultados."}
									</p>
								</div>
							</td>
						</tr>
					)}
				</tbody>
			</table>
			{totalPages > 1 && (
				<Pagination
					currentPage={currentPage}
					totalPages={totalPages}
					onPageChange={setCurrentPage}
				/>
			)}
		<Notification ref={notificationRef} />
		</div>
	)
}

export default TableEditable
